if (!customElements.get('combined-blog')) {
  class CombinedBlog extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.grid = this.querySelector('[data-combined-blog-grid]');
      this.emptyState = this.querySelector('[data-combined-blog-empty]');
      this.pagination = this.querySelector('[data-combined-blog-pagination]');
      this.status = this.querySelector('[data-combined-blog-status]');
      this.cardTemplate = this.querySelector('[data-combined-blog-card-template]');
      this.pageContainer = this.closest('.f-blog');
      this.config = this.readConfig();

      if (!this.grid || !this.emptyState || !this.pagination || !this.status || !this.cardTemplate || !this.config) return;

      this.handlePaginationClick = this.handlePaginationClick.bind(this);
      this.handleHistoryChange = this.handleHistoryChange.bind(this);
      this.addEventListener('click', this.handlePaginationClick);
      window.addEventListener('popstate', this.handleHistoryChange);
      this.loadArticles();
    }

    disconnectedCallback() {
      if (this.handlePaginationClick && this.handleHistoryChange) {
        this.removeEventListener('click', this.handlePaginationClick);
        window.removeEventListener('popstate', this.handleHistoryChange);
      }
      this.initialized = false;
    }

    readConfig() {
      const configElement = this.querySelector('[data-combined-blog-config]');

      try {
        return JSON.parse(configElement?.textContent || '');
      } catch (error) {
        console.error('Unable to read the combined blog configuration.', error);
        return null;
      }
    }

    async loadArticles() {
      this.setAttribute('aria-busy', 'true');

      try {
        const handles = [...new Set(this.config.blogHandles.filter(Boolean))];
        const articleGroups = await Promise.all(handles.map((handle) => this.fetchBlogArticles(handle)));
        const seenArticleIds = new Set();

        this.articles = articleGroups
          .flat()
          .filter((article) => {
            if (!article.onlineStoreUrl || seenArticleIds.has(article.id)) return false;

            seenArticleIds.add(article.id);
            return this.config.activeTags.every((tag) => article.tags.includes(tag));
          })
          .sort((first, second) => {
            const dateDifference = new Date(second.publishedAt) - new Date(first.publishedAt);
            return dateDifference || first.id.localeCompare(second.id);
          });

        this.renderPage(this.pageFromUrl(), { replaceInvalidPage: true });
        await this.waitForPageImages();
        this.status.hidden = true;
      } catch (error) {
        console.error('Unable to load articles from the Storefront API.', error);
        this.status.textContent = this.config.labels.error;
        this.status.hidden = false;
      } finally {
        this.setAttribute('data-combined-blog-ready', '');
        this.pageContainer?.removeAttribute('data-combined-blog-pending');
        this.removeAttribute('aria-busy');
      }
    }

    async waitForPageImages() {
      await new Promise((resolve) => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
      });

      const images = Array.from(this.grid.querySelectorAll('img[src]')).filter((image) => {
        const bounds = image.getBoundingClientRect();
        return bounds.top < window.innerHeight && bounds.bottom > 0;
      });
      if (images.length === 0) return;

      const imagePromises = images.map((image) => {
        image.loading = 'eager';

        if (typeof image.decode === 'function') {
          return image.decode().catch(() => {});
        }

        if (image.complete) return Promise.resolve();

        return new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      });

      await Promise.all(imagePromises);
    }

    async fetchBlogArticles(handle) {
      const articles = [];
      let after = null;
      let hasNextPage = true;

      while (hasNextPage) {
        const response = await fetch(this.config.endpoint, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: CombinedBlog.articlesQuery,
            variables: {
              handle,
              first: 50,
              after,
              country: this.config.country,
              language: this.config.language
            }
          })
        });

        if (!response.ok) {
          throw new Error(`Storefront API returned ${response.status}.`);
        }

        const result = await response.json();
        if (result.errors?.length) {
          throw new Error(result.errors.map((error) => error.message).join(' '));
        }

        const connection = result.data?.blog?.articles;
        if (!connection) return articles;

        articles.push(...connection.nodes);
        hasNextPage = connection.pageInfo.hasNextPage;
        after = connection.pageInfo.endCursor;

        if (hasNextPage && !after) {
          throw new Error('Storefront API did not return the next article cursor.');
        }
      }

      return articles;
    }

    renderPage(requestedPage, options = {}) {
      const totalPages = Math.ceil(this.articles.length / this.config.pageSize);

      if (totalPages === 0) {
        this.grid.replaceChildren();
        this.grid.hidden = true;
        this.emptyState.hidden = false;
        this.pagination.replaceChildren();
        this.pagination.hidden = true;
        return;
      }

      const currentPage = Math.min(Math.max(requestedPage, 1), totalPages);
      const start = (currentPage - 1) * this.config.pageSize;
      const pageArticles = this.articles.slice(start, start + this.config.pageSize);
      const cards = pageArticles.map((article, index) => this.createArticleCard(article, index));

      this.grid.replaceChildren(...cards);
      this.grid.hidden = false;
      this.emptyState.hidden = true;
      this.renderPagination(currentPage, totalPages);

      if (options.replaceInvalidPage && currentPage !== requestedPage) {
        window.history.replaceState({}, '', this.pageUrl(currentPage));
      }
    }

    createArticleCard(article, index) {
      const column = this.cardTemplate.content.firstElementChild.cloneNode(true);
      const card = column.querySelector('[data-article-card]');
      const cleanTitle = article.title.trim().replace(/\[br\/pc\]|\[br\]/g, '');

      column.querySelectorAll('[data-article-link]').forEach((link) => {
        link.href = article.onlineStoreUrl;
      });

      const imageWrapper = column.querySelector('[data-article-image-wrapper]');
      const info = column.querySelector('[data-article-info]');

      if (article.image) {
        info.classList.add('f-article-card__info--has-image');
        imageWrapper.querySelector('[data-article-image-title]').textContent = cleanTitle;

        const responsiveImage = imageWrapper.querySelector('responsive-image');
        const aspectRatio = this.imageAspectRatio(article.image);
        responsiveImage.style.setProperty('--aspect-ratio', aspectRatio);

        const image = imageWrapper.querySelector('[data-article-image]');
        image.src = article.image.w533;
        image.srcset = this.imageSrcset(article.image);
        image.alt = article.image.altText || cleanTitle;
        image.width = article.image.width || 533;
        image.height = article.image.height || 300;
      } else {
        imageWrapper.remove();
      }

      const titleLink = column.querySelector('[data-article-title]');
      titleLink.textContent = cleanTitle;
      titleLink.title = article.title;

      const category = column.querySelector('[data-article-category]');
      const categoryConfig = this.config.categories?.find(
        (item) => item.tag && item.name && article.tags.includes(item.tag)
      );
      if (category && categoryConfig) {
        category.textContent = categoryConfig.name;
        category.href = categoryConfig.url;
        if (categoryConfig.color) {
          category.style.setProperty('--article-card-category-background', categoryConfig.color);
        }
        category.hidden = false;
      } else {
        category?.remove();
      }

      const date = column.querySelector('[data-article-date]');
      if (date) {
        date.dateTime = article.publishedAt;
        date.textContent = this.formattedDate(article.publishedAt);
      }

      const excerpt = column.querySelector('[data-article-excerpt]');
      if (excerpt) {
        excerpt.textContent = this.truncateWords(article.excerpt?.trim() || article.content, 22);
      }

      const readMore = column.querySelector('[data-article-read-more]');
      if (readMore) {
        readMore.setAttribute('aria-label', this.config.labels.readMore.replace('__TITLE__', cleanTitle));
      }

      if (card.hasAttribute('data-cascade')) {
        card.style.setProperty('--animation-order', index + 1);
      }

      return column;
    }

    imageAspectRatio(image) {
      if (this.config.imageAspectRatio !== 'adapt') {
        return this.config.imageAspectRatio.replace('/', ' / ');
      }

      if (image.width && image.height) return `${image.width} / ${image.height}`;
      return '16 / 9';
    }

    imageSrcset(image) {
      const widths = [165, 360, 533, 720, 940, 1066, 1500];
      const sources = widths
        .filter((width) => !image.width || image.width >= width)
        .map((width) => `${image[`w${width}`]} ${width}w`);

      if (image.width) sources.push(`${image.original} ${image.width}w`);
      return sources.join(', ');
    }

    formattedDate(value) {
      return new Intl.DateTimeFormat(this.config.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(new Date(value));
    }

    truncateWords(value, limit) {
      const words = (value || '').trim().split(/\s+/).filter(Boolean);
      if (words.length <= limit) return words.join(' ');
      return `${words.slice(0, limit).join(' ')}…`;
    }

    renderPagination(currentPage, totalPages) {
      this.pagination.replaceChildren();

      if (totalPages <= 1) {
        this.pagination.hidden = true;
        return;
      }

      const wrapper = document.createElement('div');
      wrapper.className = 'f-pagination-wrapper text-center';
      const nav = document.createElement('nav');
      nav.className = 'f-pagination flex justify-center';
      nav.setAttribute('role', 'navigation');
      nav.setAttribute('aria-label', this.config.labels.navigation);
      const list = document.createElement('ul');
      list.className = 'f-pagination__list list-none';
      list.setAttribute('role', 'list');

      if (currentPage > 1) {
        this.addPageLink(list, currentPage - 1, this.config.labels.previous, 'previous');
      }

      const visiblePages = new Set([1, totalPages]);
      for (let page = currentPage - 2; page <= currentPage + 2; page += 1) {
        if (page > 1 && page < totalPages) visiblePages.add(page);
      }

      let previousPage = 0;
      [...visiblePages]
        .sort((first, second) => first - second)
        .forEach((page) => {
          if (page - previousPage > 1) this.addEllipsis(list);
          this.addPageLink(list, page, String(page), currentPage === page ? 'current' : 'page');
          previousPage = page;
        });

      if (currentPage < totalPages) {
        this.addPageLink(list, currentPage + 1, this.config.labels.next, 'next');
      }

      nav.appendChild(list);
      wrapper.appendChild(nav);
      this.pagination.appendChild(wrapper);
      this.pagination.hidden = false;
    }

    addPageLink(list, page, text, type) {
      const listItem = document.createElement('li');
      const isCurrent = type === 'current';
      const element = document.createElement(isCurrent ? 'span' : 'a');
      element.className = 'f-pagination__item';
      element.setAttribute('aria-label', this.config.labels.page.replace('__PAGE__', page));

      if (isCurrent) {
        element.classList.add('f-pagination__item--current');
        element.setAttribute('aria-current', 'page');
      } else {
        element.href = this.pageUrl(page);
        element.dataset.combinedBlogPage = page;
      }

      if (type === 'previous' || type === 'next') {
        element.classList.add(`f-pagination__item--${type === 'previous' ? 'prev' : 'next'}`);
        element.setAttribute('aria-label', text);
        const label = document.createElement('span');
        label.textContent = text;

        if (type === 'previous') {
          element.append(this.arrowIcon('previous'), label);
        } else {
          element.append(label, this.arrowIcon('next'));
        }
      } else {
        element.textContent = text;
      }

      listItem.appendChild(element);
      list.appendChild(listItem);
    }

    addEllipsis(list) {
      const listItem = document.createElement('li');
      const ellipsis = document.createElement('span');
      ellipsis.className = 'f-pagination__item';
      ellipsis.textContent = '…';
      listItem.appendChild(ellipsis);
      list.appendChild(listItem);
    }

    arrowIcon(direction) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '6');
      svg.setAttribute('height', '10');
      svg.setAttribute('viewBox', '0 0 6 10');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('aria-hidden', 'true');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', direction === 'previous' ? 'M4.5 1.25L0.75 5L4.5 8.75' : 'M1.5 1.25L5.25 5L1.5 8.75');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.5');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      svg.appendChild(path);
      return svg;
    }

    pageFromUrl() {
      const page = Number.parseInt(new URL(window.location.href).searchParams.get('page'), 10);
      return Number.isInteger(page) && page > 0 ? page : 1;
    }

    pageUrl(page) {
      const url = new URL(window.location.href);

      if (page === 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', page);
      }

      return `${url.pathname}${url.search}${url.hash}`;
    }

    async handlePaginationClick(event) {
      const link = event.target.closest('[data-combined-blog-page]');
      if (!link || !this.contains(link)) return;

      event.preventDefault();
      const page = Number.parseInt(link.dataset.combinedBlogPage, 10);
      window.history.pushState({}, '', this.pageUrl(page));
      await this.showPage(page);
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    async handleHistoryChange() {
      if (!this.articles) return;
      await this.showPage(this.pageFromUrl());
    }

    async showPage(page) {
      this.pageContainer?.setAttribute('data-combined-blog-pending', '');
      this.setAttribute('aria-busy', 'true');

      try {
        this.renderPage(page);
        await this.waitForPageImages();
      } finally {
        this.pageContainer?.removeAttribute('data-combined-blog-pending');
        this.removeAttribute('aria-busy');
      }
    }
  }

  CombinedBlog.articlesQuery = `
      query BlogArticles(
        $handle: String!
        $first: Int!
        $after: String
        $country: CountryCode!
        $language: LanguageCode!
      ) @inContext(country: $country, language: $language) {
        blog(handle: $handle) {
          articles(first: $first, after: $after, sortKey: PUBLISHED_AT, reverse: true) {
            nodes {
              id
              title
              onlineStoreUrl
              publishedAt
              excerpt
              content(truncateAt: 500)
              tags
              image {
                altText
                width
                height
                original: url
                w165: url(transform: { maxWidth: 165 })
                w360: url(transform: { maxWidth: 360 })
                w533: url(transform: { maxWidth: 533 })
                w720: url(transform: { maxWidth: 720 })
                w940: url(transform: { maxWidth: 940 })
                w1066: url(transform: { maxWidth: 1066 })
                w1500: url(transform: { maxWidth: 1500 })
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    `;

  customElements.define('combined-blog', CombinedBlog);
}
